import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useInView } from "../../hooks/useInView";
import StripeBillingGate from "../StripeBillingGate";

// Security constants
const SUBMISSION_COOLDOWN = 1000; // 1 second between API calls

// Validate URL parameters
const validateStudioId = (studioId: string | null): boolean => {
  if (!studioId || typeof studioId !== 'string') return false;
  // UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(studioId);
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

type IncomeBreakdown = {
  stripe: { revenue: number; studentCount: number; avgIncome: number };
  manual: { revenue: number; studentCount: number; avgIncome: number };
  total: { revenue: number; studentCount: number };
};

export default function AverageIncomeCard({
  studioId,
  isStripeConnected,
  canUseStripeBilling = true,
}: {
  studioId: string | null;
  isStripeConnected: boolean | null;
  canUseStripeBilling?: boolean;
}) {
  const { getAuthToken } = useAuth();
  const { ref, inView } = useInView();
  const [loading, setLoading] = useState(true);
  const [avgIncomePerStudent, setAvgIncomePerStudent] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<IncomeBreakdown | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  useEffect(() => {
    if (!canUseStripeBilling || isStripeConnected === false) {
      setLoading(false);
    }
  }, [canUseStripeBilling, isStripeConnected]);

  useEffect(() => {
    const run = async () => {
      if (
        !inView ||
        !studioId ||
        !canUseStripeBilling ||
        !isStripeConnected ||
        avgIncomePerStudent !== null
      ) {
        return;
      }
      
      // Security validations
      if (!validateStudioId(studioId)) {
        logSecurityEvent('INVALID_STUDIO_ID', { studioId });
        return;
      }
      
      // Rate limiting
      const now = Date.now();
      if (now - lastFetchTime < SUBMISSION_COOLDOWN) {
        return;
      }
      
      const token = getAuthToken();
      if (!token) return;
      
      setLoading(true);
      setLastFetchTime(now);
      
      try {
        // Use encodeURIComponent for URL parameters
        const encodedStudioId = encodeURIComponent(studioId);
        
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/metrics/${encodedStudioId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.ok) {
          const json = await res.json();
          // Validate and sanitize numeric value
          const avgIncome = typeof json.avgIncomePerStudent === 'number' && json.avgIncomePerStudent >= 0 ? json.avgIncomePerStudent : 0;
          setAvgIncomePerStudent(avgIncome);
          
          // Store breakdown if available
          if (json.breakdown) {
            setBreakdown(json.breakdown);
          }
        } else {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: res.status, 
            statusText: res.statusText,
            endpoint: 'stripe/metrics'
          });
        }
      } catch (error) {
        logSecurityEvent('API_REQUEST_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error',
          endpoint: 'stripe/metrics'
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [inView, studioId, canUseStripeBilling, isStripeConnected, avgIncomePerStudent, getAuthToken, lastFetchTime]);

  return (
    <div ref={ref} className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Average Student Membership Income</h3>
      <div className="flex flex-col items-center justify-center h-64">
        {(!inView || loading) && canUseStripeBilling && isStripeConnected ? (
          <div className="flex justify-center items-center">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Loading income data...</div>
            </div>
          </div>
        ) : !canUseStripeBilling || isStripeConnected !== true ? (
          <StripeBillingGate
            canUseStripeBilling={canUseStripeBilling}
            isStripeConnected={isStripeConnected}
            variant="card"
          />
        ) : (
          <>
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
              ${
                (avgIncomePerStudent ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">per student per month</p>
            
            {breakdown && (breakdown.stripe.studentCount > 0 || breakdown.manual.studentCount > 0) && (
              <div className="w-full max-w-sm space-y-2 mt-4">
                {breakdown.stripe.studentCount > 0 && (
                  <div className="flex items-center justify-between text-xs bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💳</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Auto Billing</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600 dark:text-blue-400">
                        ${breakdown.stripe.avgIncome.toFixed(2)}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {breakdown.stripe.studentCount} student{breakdown.stripe.studentCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                )}
                
                {breakdown.manual.studentCount > 0 && (
                  <div className="flex items-center justify-between text-xs bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💵</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Cash/Manual</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600 dark:text-green-400">
                        ${breakdown.manual.avgIncome.toFixed(2)}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {breakdown.manual.studentCount} student{breakdown.manual.studentCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

